import React, {
  useState,
  useRef,
  useMemo,
  memo,
  useEffect,
  useContext,
  useCallback
} from 'react';
import classNames from 'classnames';
import { Document, Page } from 'react-pdf';
import VirtualList, { ListRef } from 'rc-virtual-list';
import {
  OverlayScrollbarsComponent,
  OverlayScrollbarsComponentRef
} from 'overlayscrollbars-react';
import { ReactComponent as SlideTextSvg } from '@assets/icons/slide-text.svg';
import { ReactComponent as ArrowRightSvg } from '@assets/icons/arrow-right-without-line.svg';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as PagesSvg } from '@assets/icons/MediaViewer/pages.svg';
import type { fabric } from 'fabric';
import { setLatestZoomOptionFromStorage } from '@context/AssetViewerStateContext';
import PanelAction from '@pages/MediaViewer/AssetViewer/PanelAction';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { DocumentMetadataDto } from '@api/Api';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import PageThumbnail from '@pages/MediaViewer/AssetViewer/PdfCard/components/PageThumbnail';
import 'react-pdf/dist/Page/TextLayer.css';
import { highlightCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';
import type { AssetViewerZoomOption } from '@context/AssetViewerStateContext/types';
import {
  getInitialPdfPageScale,
  handleSelectionChange,
  hasRenderedTextLayerText
} from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import type { PdfCardProps } from '@pages/MediaViewer/AssetViewer/PdfCard/PdfCard';

const maxCanvasSize = 2048;

const minZoom = 0.1;
const maxZoom = 3;

function extractTextFromNode(node: Node): string {
  if (node.nodeName === 'BR') return '\n';
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
  if (node.nodeType === Node.ELEMENT_NODE)
    return Array.from(node.childNodes).map(extractTextFromNode).join('');
  return '';
}

export default memo(function PdfCardDesktop({
  isExpand,
  setIsExpand,
  onReady,
  asset,
  isAssetReady,
  mobileActionVisibility
}: PdfCardProps) {
  const fabricContext = useContext(FabricContext);
  const {
    selectedCommentId,
    shapesDrawingTools,
    activePageNumber,
    zoomFactor,
    zoomOption,
    turnoverDegree,
    documentHighlight,
    selectComment,
    setActivePageNumber,
    setDocumentHighlight,
    setTurnoverDegree,
    setZoomFactor,
    setZoomOption,
    setCanvasMode,
    setActivePageHasText
  } = useAssetViewerState();
  const isShapeDrawing = shapesDrawingTools.shape !== 'cursor';
  const documentHighlightRef = useRef(documentHighlight);
  const zoomRef = useRef(zoomFactor);
  zoomRef.current = zoomFactor;

  const listRef = useRef<ListRef>(null);
  const wrapperRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);
  const isLoadPdf = useRef<boolean>(false);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const buttonSelectedTextRef = useRef<HTMLButtonElement>(null);

  const textLayersMapRef = useRef<Map<HTMLDivElement, HTMLDivElement>>(
    new Map()
  );

  const fileUrl = useMemo(() => {
    const { proxy } = asset.metadata as DocumentMetadataDto;
    return proxy.url || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  useEffect(() => {
    isLoadPdf.current = false;
    setIsPageSizeReady(false);
  }, [asset.id]);

  const [pagesCount, setPagesCount] = useState(1);

  const [isShowPages, setIsShowPages] = useState<boolean>(true);
  const [isPageSizeReady, setIsPageSizeReady] = useState(false);
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({
    width: 1605,
    height: 2272
  });

  const _pageSize = useMemo(() => {
    return {
      width:
        pageSize.width > pageSize.height
          ? pageSize.width * zoomFactor
          : undefined,
      height:
        pageSize.height > pageSize.width
          ? pageSize.height * zoomFactor
          : undefined
    };
  }, [pageSize, zoomFactor]);

  const pagesArray = useMemo(
    () =>
      new Array(pagesCount).fill('').map((_, index) => ({ page: index + 1 })),
    [pagesCount]
  );

  const onSelectText = (
    e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    setDocumentHighlight(documentHighlightRef.current);
  };

  const onClearHighlightObjects = useCallback(() => {
    const { canvas } = fabricContext;
    if (!canvas) return;
    const getHighlightObjects = canvas
      .getObjects()
      .filter((item) => item.type === 'highlight');
    getHighlightObjects.forEach((obj) => canvas.remove(obj));
    canvas.fire('object:history:remove');
  }, [fabricContext.canvas]);

  const renderTextHighlights = useCallback(
    (rects: any) => {
      if (!fabricContext.canvas) return;
      onClearHighlightObjects();
      highlightCreator({
        canvas: fabricContext.canvas,
        documentHighlightRects: rects,
        viewerActions: {
          selectComment,
          setCanvasMode,
          setDocumentHighlight
        }
      });
    },
    [fabricContext.canvas, onClearHighlightObjects]
  );

  useEffect(() => {
    if (!selectedCommentId) return;
    if (
      documentHighlightRef.current?.rects?.length &&
      !documentHighlight.rects?.length
    ) {
      onClearHighlightObjects();
    }
  }, [selectedCommentId, documentHighlight.rects, onClearHighlightObjects]);

  useEffect(() => {
    if (!isShapeDrawing) return;
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    const buttonSelectedText = buttonSelectedTextRef.current;
    const pageCanvas = pageCanvasRef.current;
    let prevRange: Range | null = null;

    const onSelectionchange = () => {
      textLayersMapRef.current.clear();
      const pageRoot = documentRef.current;
      const textLayerDiv = pageRoot?.querySelector('.textLayer');
      const endDiv = pageRoot?.querySelector('.endOfContent');
      if (!textLayerDiv || !endDiv) return;
      textLayersMapRef.current.set(
        textLayerDiv as HTMLDivElement,
        endDiv as HTMLDivElement
      );

      const range = handleSelectionChange(textLayersMapRef.current, prevRange);
      prevRange = range;

      if (buttonSelectedText) {
        buttonSelectedText.style.display = 'none';
        buttonSelectedText.style.pointerEvents = 'none';
      }

      if (!range) {
        onClearHighlightObjects();
        return;
      }

      const pageCoordsElement =
        pageRoot?.querySelector('.media_viewer_pdf-inner') ?? pageCanvas;

      if (!pageCoordsElement) {
        onClearHighlightObjects();
        return;
      }

      const rects = Array.from(range.getClientRects()).filter(
        (r) => r.width > 0 && r.height > 0
      );
      if (!rects.length) {
        onClearHighlightObjects();
        if (buttonSelectedText) {
          buttonSelectedText.style.display = 'none';
        }
        return;
      }
      const canvasRect = pageCoordsElement.getBoundingClientRect();
      const selectedCoords = rects
        .map((rect) => ({
          left: ((rect.left - canvasRect.left) / canvasRect.width) * 100,
          top: ((rect.top - canvasRect.top) / canvasRect.height) * 100,
          width: (rect.width / canvasRect.width) * 100,
          height: (rect.height / canvasRect.height) * 100
        }))
        .filter((r) => r.width > 0 && r.height > 0);

      if (!selectedCoords.length) {
        onClearHighlightObjects();
        return;
      }

      const fragment = range.cloneContents();
      documentHighlightRef.current = {
        text: Array.from(fragment.childNodes)
          .map(extractTextFromNode)
          .join('')
          .trim(),
        rects: selectedCoords
      };
      renderTextHighlights(selectedCoords);
      if (!textLayerDiv) return;
      const containerRect = textLayerDiv.getBoundingClientRect();
      const lastRect = rects[rects.length - 1];
      const buttonWidth = buttonSelectedText?.offsetWidth || 0;
      const buttonHeight = buttonSelectedText?.offsetHeight || 0;
      const left = Math.min(
        Math.max(lastRect.right - containerRect.left + 8, 0),
        containerRect.width - buttonWidth
      );
      const top = Math.min(
        Math.max(lastRect.bottom - containerRect.top + 8, 0),
        containerRect.height - buttonHeight
      );
      if (buttonSelectedText) {
        buttonSelectedText.style.display = 'block';
        buttonSelectedText.style.left = `${left}px`;
        buttonSelectedText.style.top = `${top}px`;
      }
    };

    const onMouseUp = () => {
      if (!buttonSelectedText) return;
      buttonSelectedText.style.pointerEvents = 'auto';
    };

    const onTouchEnd = () => {
      if (!buttonSelectedText) return;
      buttonSelectedText.style.pointerEvents = 'auto';
    };

    document.addEventListener('selectionchange', onSelectionchange);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      prevRange = null;
      textLayersMapRef.current.clear();
      document.removeEventListener('selectionchange', onSelectionchange);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [
    selectedCommentId,
    shapesDrawingTools,
    isShapeDrawing,
    fabricContext.canvas,
    activePageNumber,
    setDocumentHighlight,
    onClearHighlightObjects,
    renderTextHighlights
  ]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({
      index: activePageNumber - 1
    });
  }, [isShowPages, activePageNumber]);

  useEffect(() => {
    isLoadPdf.current = false;
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    viewport?.scroll({ top: 0, left: 0 });
  }, [activePageNumber]);

  useEffect(() => {
    if (!selectedCommentId) return;
    if (selectedCommentId === 'new') return;
    const intId = setTimeout(() => {
      const getObjects = fabricContext.getObjects() || [];
      const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
      if (!getObjects.length || !viewport) return;
      const firstObject: fabric.Object = getObjects[0];
      const objectCoord = firstObject.getCoords()[0];
      const coord = {
        x: objectCoord.x - firstObject.getScaledWidth(),
        y: objectCoord.y - firstObject.getScaledHeight()
      };
      const isInViewport =
        coord.x >= viewport.scrollLeft &&
        coord.x <= viewport.clientWidth &&
        coord.y >= viewport.scrollTop &&
        coord.y <= viewport.scrollTop + viewport.clientHeight;
      if (!isInViewport) viewport.scroll({ top: coord.y, left: coord.x });
    }, 250);
    return () => {
      clearTimeout(intId);
    };
  }, [selectedCommentId, documentHighlight.rects]);

  const getZoomFactorFromOption = useCallback(
    (option: NonNullable<AssetViewerZoomOption>) => {
      if (option === 'actual-size') return 1;
      const container = wrapperRef?.current?.getElement();
      if (!container || !isPageSizeReady) return 1;
      const width =
        Math.abs(pageSize.width * Math.cos(turnoverDegree * (Math.PI / 180))) +
        Math.abs(pageSize.height * Math.sin(turnoverDegree * (Math.PI / 180)));
      const height =
        Math.abs(pageSize.width * Math.sin(turnoverDegree * (Math.PI / 180))) +
        Math.abs(pageSize.height * Math.cos(turnoverDegree * (Math.PI / 180)));
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const scaleWidth = Math.floor(containerWidth / (width / 100)) / 100;
      const scaleHeight = Math.floor(containerHeight / (height / 100)) / 100;
      if (option === 'auto' || option === 'fit-width') {
        return scaleWidth;
      }
      if (option === 'fit-height') {
        return scaleHeight;
      }
      return 1;
    },
    [pageSize, turnoverDegree, isPageSizeReady]
  );

  const setZoom = useCallback(
    (v: number | NonNullable<AssetViewerZoomOption>) => {
      if (typeof v === 'number') {
        setZoomFactor(v);
      } else {
        const newZoomFactor = getZoomFactorFromOption(v);
        setZoomFactor(newZoomFactor, v);
      }
    },
    [getZoomFactorFromOption, setZoomFactor]
  );

  useEffect(() => {
    setZoomOption(setLatestZoomOptionFromStorage());
  }, [setZoomOption]);

  useEffect(() => {
    if (!zoomOption || !isPageSizeReady) return undefined;

    const container = wrapperRef.current?.getElement();
    if (!container) return undefined;

    const applyZoom = () => {
      const newZoomFactor = getZoomFactorFromOption(zoomOption);
      setZoomFactor(newZoomFactor, zoomOption);
    };

    applyZoom();
    const observer = new ResizeObserver(applyZoom);
    observer.observe(container);
    return () => observer.disconnect();
  }, [zoomOption, getZoomFactorFromOption, setZoomFactor, isPageSizeReady]);

  useEffect(() => {
    const documentEl = documentRef?.current;
    const content = contentRef?.current;
    const canvasContainer = canvasContainerRef?.current;
    if (!documentEl || !canvasContainer || !content || !fabricContext.canvas)
      return;

    canvasContainer.style.removeProperty('left');
    canvasContainer.style.removeProperty('top');

    const rect = documentEl.getBoundingClientRect();
    canvasContainer.style.width = `${rect.width}px`;
    canvasContainer.style.height = `${rect.height}px`;
    canvasContainer.style.translate = `-${Math.round(
      rect.width / 2
    )}px -${Math.round(rect.height / 2)}px`;
    content.style.width = `${rect.width}px`;
    content.style.height = `${rect.height}px`;
    fabricContext.canvas.setWidth(rect.width);
    fabricContext.canvas.setHeight(rect.height);
    fabricContext.canvas.setViewportTransform([
      zoomFactor * 10,
      0,
      0,
      zoomFactor * 10,
      0,
      0
    ]);
  }, [pageSize, zoomFactor, fabricContext.canvas, turnoverDegree]);

  useEffect(() => {
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    if (!documentHighlightRef.current?.rects?.length) return;
    renderTextHighlights(documentHighlightRef.current.rects);
  }, [zoomFactor, shapesDrawingTools.shape, renderTextHighlights]);

  const onResetPosition = useCallback(() => {
    isLoadPdf.current = false;
  }, []);

  const onChangePage = useCallback(
    (page: number) => {
      onResetPosition();
      setActivePageNumber(page);
      selectComment(null);
      setDocumentHighlight(null);
      setCanvasMode('view');
    },
    [
      onResetPosition,
      setActivePageNumber,
      selectComment,
      setDocumentHighlight,
      setCanvasMode
    ]
  );

  const pageCanvasRef = useRef<HTMLCanvasElement>(null);
  const onLoadSuccess = useCallback(
    (pdfPage: { originalWidth: number; originalHeight: number }) => {
      if (isLoadPdf.current) return;
      isLoadPdf.current = true;
      const { originalWidth, originalHeight } = pdfPage;
      setPageSize(
        getInitialPdfPageScale(
          originalWidth,
          originalHeight,
          maxCanvasSize,
          maxZoom,
          false
        )
      );
      setIsPageSizeReady(true);
    },
    []
  );

  const onToggleShowPages = useCallback(() => {
    setIsShowPages((prevIsShowPages) => !prevIsShowPages);
  }, [setIsShowPages]);

  const showPanelAction =
    !mobileActionVisibility || mobileActionVisibility.showPanelAction;

  const panelActionConfig = useMemo(
    () => ({
      zoom: zoomFactor,
      zoomOption,
      setZoom,
      isShapeDrawingToolsOpen: isShapeDrawing,
      isExpand,
      setIsExpand: (v: boolean) => {
        onResetPosition();
        setIsExpand(v);
      },
      setRotate: (v: number) => {
        setTurnoverDegree(turnoverDegree + v === 360 ? 0 : turnoverDegree + v);
      }
    }),
    [
      zoomFactor,
      zoomOption,
      setZoom,
      isShapeDrawing,
      isExpand,
      onResetPosition,
      setIsExpand,
      turnoverDegree,
      setTurnoverDegree
    ]
  );

  return (
    <Document
      className={classNames('media_viewer_pdf', {
        'b-show-pages': isShowPages,
        'b-hide-shapes-drawing':
          shapesDrawingTools?.shape === 'documentHighlight' && isShapeDrawing
      })}
      file={fileUrl}
      onLoadSuccess={(pdf) => {
        setPagesCount(pdf.numPages);
        onReady();
      }}
      loading={<PlaybackPreparingLoader />}
    >
      {isShowPages && (
        <div className="media_viewer_pdf-sidebar">
          <VirtualList
            className="media_viewer_pdf-list"
            data={pagesArray}
            height={wrapperRef?.current?.getElement()?.offsetHeight || 500}
            fullHeight={true}
            itemKey="page"
            ref={listRef}
          >
            {(item) => {
              return (
                <PageThumbnail
                  page={item.page}
                  active={item.page === activePageNumber}
                  maxWidth={102}
                  maxHeight={147}
                  onClick={onChangePage}
                />
              );
            }}
          </VirtualList>
        </div>
      )}
      <div className="media_viewer_pdf-actions">
        <div className="media_viewer_pdf-actions-indicator">
          <button
            className="media_viewer_pdf-btn"
            type="button"
            disabled={activePageNumber === 1}
            onClick={() => onChangePage(activePageNumber - 1)}
          >
            <ArrowLeftSvg width={24} height={24} />
          </button>
          {activePageNumber} of {pagesCount}
          <button
            disabled={activePageNumber === pagesCount}
            className="media_viewer_pdf-btn"
            type="button"
            onClick={() => onChangePage(activePageNumber + 1)}
          >
            <ArrowRightSvg width={24} height={24} />
          </button>
        </div>
        <button
          className="media_viewer_pdf-btn media_viewer_pdf-action-btn tour-text-highlight-pdf-action"
          type="button"
          onClick={() => setIsShowPages(!isShowPages)}
        >
          <PagesSvg />
        </button>
      </div>
      {showPanelAction && <PanelAction {...panelActionConfig} />}
      <OverlayScrollbarsComponent
        className="media_viewer_pdf-wrapper"
        ref={wrapperRef}
      >
        <div className="media_viewer_pdf-content" ref={contentRef}>
          <div
            className="media_viewer_pdf-inner"
            ref={documentRef}
            style={{
              transform: `rotate(${turnoverDegree}deg)`,
              ..._pageSize
            }}
          >
            <Page
              width={_pageSize.width}
              height={_pageSize.height}
              scale={1}
              pageNumber={activePageNumber}
              className="media_viewer_pdf-page"
              canvasRef={pageCanvasRef}
              renderAnnotationLayer={false}
              onLoadSuccess={onLoadSuccess}
              onRenderTextLayerSuccess={() => {
                setActivePageHasText(
                  hasRenderedTextLayerText(documentRef.current)
                );
              }}
              loading={<PlaybackPreparingLoader />}
            />
          </div>
          <button
            ref={buttonSelectedTextRef}
            className="media_viewer_pdf-selected-btn"
            type="button"
            onClick={onSelectText}
            style={{ display: 'none' }}
          >
            <SlideTextSvg />
          </button>
          <div ref={canvasContainerRef} className="media_viewer_pdf-canvas">
            <FabricCanvas />
          </div>
        </div>
      </OverlayScrollbarsComponent>
      {!isAssetReady && <PlaybackPreparingLoader />}
    </Document>
  );
});
